import React from 'react';
import styled from 'styled-components';

interface InlineEditableTextProps {
    value: string;
    onSubmit: (value: string) => void | Promise<void>;
    onCancel?: () => void;
    isSaving?: boolean;
    multiline?: boolean;
    autoFocus?: boolean;
    ariaLabel?: string;
    placeholder?: string;
}

const TextInput = styled.input`
    width: 100%;
    padding: 6px 10px;
    border-radius: 8px;
    border: 1px solid #cbd5f5;
    background: #ffffff;
    font-size: 14px;
    color: #0f172a;

    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
    }
`;

const TextArea = styled.textarea`
    width: 100%;
    min-height: 80px;
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid #cbd5f5;
    background: #ffffff;
    font-size: 14px;
    color: #0f172a;
    resize: vertical;

    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
    }
`;

const InlineForm = styled.form`
    display: inline-flex;
    align-items: stretch;
    gap: 8px;
    width: 100%;
`; 

export const InlineEditableText: React.FC<InlineEditableTextProps> = ({
    value,
    onSubmit,
    onCancel,
    isSaving = false,
    multiline = false,
    autoFocus = false,
    ariaLabel,
    placeholder,
}) => {
    const [draft, setDraft] = React.useState<string>(value);

    React.useEffect(() => {
        setDraft(value);
    }, [value]);

    const handleSubmit = React.useCallback(
        async (event?: React.FormEvent) => {
            event?.preventDefault();
            if (isSaving) {
                return;
            }
            const trimmed = multiline ? draft.trimEnd() : draft.trim();
            console.debug('[InlineEditableText] submit attempt', {
                ariaLabel,
                trimmed,
                original: value,
            });
            if (trimmed === value.trim()) {
                onCancel?.();
                return;
            }
            await onSubmit(trimmed);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps -- ariaLabel is only used for logging
        [draft, isSaving, multiline, onSubmit, onCancel, value],
    );

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                setDraft(value);
                onCancel?.();
                return;
            }
            if (!multiline && event.key === 'Enter') {
                event.preventDefault();
                void handleSubmit();
            }
            if (multiline && (event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                event.preventDefault();
                void handleSubmit();
            }
        },
        [handleSubmit, multiline, onCancel, value],
    );

    const handleBlur = React.useCallback(() => {
        void handleSubmit();
    }, [handleSubmit]);

    return (
        <InlineForm onSubmit={handleSubmit}>
            {multiline ? (
                <TextArea
                    value={draft}
                    onChange={event => setDraft(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    disabled={isSaving}
                    autoFocus={autoFocus}
                    aria-label={ariaLabel}
                    placeholder={placeholder}
                />
            ) : (
                <TextInput
                    value={draft}
                    onChange={event => setDraft(event.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                    disabled={isSaving}
                    autoFocus={autoFocus}
                    aria-label={ariaLabel}
                    placeholder={placeholder}
                />
            )}
        </InlineForm>
    );
};

export default InlineEditableText;
